import { put, all, takeEvery, fork, select, call } from "redux-saga/effects";
import { actions, getters } from "../model";
import { registerError } from "redux/modules/errors/actions";

import { apiCall } from "App/Data/sagas";

import { eventDetails as getEventDetails } from "redux/modules/event/selectors";
import { eventDetails as getPortalEventDetails } from "redux/modules/portal/selectors";
import { showSnackbar } from "redux/modules/snackbar/actions";
import { getters as FormGetters } from "ui-kit/Form/model";

import fileUploadSagas from "./fileUploadSagas";

import { EDIT_TYPE_FORM_ID, VIEW_TYPES } from "../constants";

const getParams = function*() {
  const viaPortal = yield select(getters.viaPortal);
  const eventDetails = yield select(getEventDetails);
  const portalEventDetails = yield select(getPortalEventDetails);

  return {
    eventDetails: viaPortal ? portalEventDetails : eventDetails,
    viaPortal
  };
};

const refreshContent = function*() {
  try {
    yield put(actions.setRefreshing(true));

    const { eventDetails } = yield call(getParams);
    const sessionId = yield select(getters.sessionId);

    const { payload: contentPayload } = yield call(apiCall, {
      method: "get",
      url: `/portal/event/${eventDetails.uuid}/session-content/${sessionId}/content`
    });

    yield put(actions.setContent(contentPayload));
  } catch (error) {
    yield all([
      put(
        registerError([
          {
            system: error,
            user: "An error occurred getting content"
          }
        ])
      )
    ]);
  } finally {
    yield put(actions.setRefreshing(false));
  }
};

const init = function*({ payload: { sessionId, viaPortal } }) {
  try {
    yield all([
      put(actions.setLoading(true)),
      put(actions.setSessionId(sessionId)),
      put(actions.setViaPortal(viaPortal)),
      put(actions.setView(VIEW_TYPES.LIST))
    ]);

    const { eventDetails } = yield call(getParams);

    const { payload: contentPayload } = call(apiCall, {
      method: "get",
      url: `/portal/event/${eventDetails.uuid}/session-content/${sessionId}/content`
    });

    yield put(
      actions.setInitialData({
        content: contentPayload
      })
    );
  } catch (error) {
    yield all([
      put(
        registerError([
          {
            system: error,
            user: "An error occurred getting content settings"
          }
        ])
      )
    ]);
  } finally {
    yield put(actions.setLoading(false));
  }
};

const sendComment = function*({ payload: { newComment, idFileVersion } }) {
  const sessionId = yield select(getters.sessionId);
  const { eventDetails, viaPortal } = yield call(getParams);
  try {
    yield call(apiCall, {
      method: "post",
      url: `/portal/event/${eventDetails.uuid}/session-content/${sessionId}/content/${idFileVersion}/comments`,
      data: {
        message: newComment,
        role: viaPortal ? "participant" : "admin"
      }
    });

    yield call(refreshContent);
  } catch (error) {
    yield all([
      put(
        registerError([
          {
            system: error,
            user: "An error occurred sending your comment"
          }
        ])
      )
    ]);
  }
};

const downloadFile = function*({ payload: fileId }) {
  const content = yield select(getters.combinedContent);
  const file = content.find(c => c.id === fileId);
  window.open(file.url, "_blank");
};

const deleteFile = function*({ payload: fileId }) {
  const { eventDetails } = yield call(getParams);
  const sessionId = yield select(getters.sessionId);

  try {
    yield call(apiCall, {
      method: "delete",
      url: `/portal/event/${eventDetails.uuid}/session-content/${sessionId}/content/${fileId}`
    });
    yield call(refreshContent);
    yield put(showSnackbar({ message: "File version has been deleted" }));
  } catch (error) {
    yield all([
      put(
        registerError([
          {
            system: error,
            user: "An error occurred deleting file version"
          }
        ])
      )
    ]);
  }
};

const deleteAllVersions = function*({ payload: fileId }) {
  const { eventDetails } = yield call(getParams);
  const sessionId = yield select(getters.sessionId);

  try {
    yield call(apiCall, {
      method: "delete",
      url: `/portal/event/${eventDetails.uuid}/session-content/${sessionId}/content/${fileId}`,
      data: { deleteAllVersions: true }
    });
    yield call(refreshContent);
    yield put(showSnackbar({ message: "File has been deleted" }));
  } catch (error) {
    yield all([
      put(
        registerError([
          {
            system: error,
            user: "An error occurred deleting file"
          }
        ])
      )
    ]);
  }
};

const saveUpdatedType = function*() {
  const sessionId = yield select(getters.sessionId);
  const fileId = yield select(getters.showEditTypeModal);
  const { eventDetails } = yield call(getParams);
  const fields = yield select(FormGetters.fields, {
    instanceId: EDIT_TYPE_FORM_ID
  });

  try {
    yield call(apiCall, {
      method: "put",
      url: `/portal/event/${eventDetails.uuid}/session-content/${sessionId}/content/${fileId}`,
      data: { type: fields.type.value[0] }
    });
    yield put(actions.setShowEditTypeModal(false));
    yield call(refreshContent);
    yield put(showSnackbar({ message: "File Type Updated" }));
  } catch (error) {
    yield all([
      put(
        registerError([
          {
            system: error,
            user: "An error occurred updating file type"
          }
        ])
      )
    ]);
  }
};

const watchInit = function*() {
  yield takeEvery(actions.init.type, init);
};

const watchRefreshContent = function*() {
  yield takeEvery(actions.refreshContent.type, refreshContent);
};

const watchSendComment = function*() {
  yield takeEvery(actions.sendComment.type, sendComment);
};

const watchDownloadFile = function*() {
  yield takeEvery(actions.downloadFile.type, downloadFile);
};

const watchDeleteFile = function*() {
  yield takeEvery(actions.deleteFile.type, deleteFile);
};

const watchDeleteAllVersions = function*() {
  yield takeEvery(actions.deleteAllVersions.type, deleteAllVersions);
};

const watchSaveUpdatedType = function*() {
  yield takeEvery(actions.saveUpdatedType.type, saveUpdatedType);
};

const rootSaga = function*() {
  yield all([
    fork(fileUploadSagas),
    fork(watchInit),
    fork(watchRefreshContent),
    fork(watchSendComment),
    fork(watchDownloadFile),
    fork(watchDeleteFile),
    fork(watchDeleteAllVersions),
    fork(watchSaveUpdatedType)
  ]);
};

export default rootSaga;
